import React from 'react';
import { FloatingMenu, Forms, SystemText } from 'e4shared';
import { useSelector } from 'react-redux';

export const GroupPicker = ({
    limitTypes,
    label,
    help,
    error,
    mandatory,
    managersOnly,
    allowMultiple,
    onChange,
    value,
    showUserOption,
    controlId,
}) => {
    const [type, setType] = React.useState('R');
    const [typeName, setTypeName] = React.useState('Roles');
    const [profileIds, setProfileIds] = React.useState([]);
    const [roleIds, setRoleIds] = React.useState([]);
    const [teamIds, setTeamIds] = React.useState([]);
    const [reportIds, setReportIds] = React.useState([]);
    const [departmentIds, setDepartmentIds] = React.useState([]);
    const [locationIds, setLocationIds] = React.useState([]);
    const [unitIds, setUnitIds] = React.useState([]);
    const [init, setInit] = React.useState(false);

    const user = useSelector(state => state.user.user);
    const showFormHelp = useSelector(state => state.ui.showFormHelp);

    const defaultLabel = SystemText('General/Controls/GroupPicker/Label');

    React.useEffect(() => {
        setTypeName(types.find(t => t.id === type)?.name);

        if (!allowMultiple) {
            type !== 'P' && setProfileIds([]);
            type !== 'R' && setRoleIds([]);
            type !== 'T' && setTeamIds([]);
            type !== 'X' && setReportIds([]);
            type !== 'D' && setDepartmentIds([]);
            type !== 'L' && setLocationIds([]);
            type !== 'B' && setUnitIds([]);
        }
    }, [type]);

    const types = [
        { id: 'P', icon: 'user', collection: 'profileIds' },
        { id: 'R', icon: 'circle-user', collection: 'roleIds' },
        { id: 'T', icon: 'sitemap', collection: 'teamIds' },
        { id: 'X', icon: 'users', collection: 'reportIds' },
        { id: 'D', icon: 'building', collection: 'departmentIds' },
        { id: 'L', icon: 'map-marker', collection: 'locationIds' },
        { id: 'B', icon: 'cubes', collection: 'unitIds' },
    ].map(t => ({
        ...t,
        name: SystemText(`General/Controls/GroupPicker/Types/${t.id}`),
        abbr: SystemText(`General/Controls/GroupPicker/Types/Abbr/${t.id}`),
    }));

    const getCount = x => {
        switch (x) {
            case 'P':
                return profileIds?.length;
            case 'R':
                return roleIds?.length;
            case 'T':
                return teamIds?.length;
            case 'X':
                return reportIds?.length;
            case 'D':
                return departmentIds?.length;
            case 'L':
                return locationIds?.length;
            case 'B':
                return unitIds?.length;
            default:
                return 0;
        }
    };

    const getCollectionCount = x => {
        switch (x) {
            case 'P':
                return profiles?.length;
            case 'R':
                return roles?.length;
            case 'T':
            case 'X':
                return teams?.length;
            case 'D':
                return departments?.length;
            case 'L':
                return locations?.length;
            case 'B':
                return units?.length;
            default:
                return 0;
        }
    };

    const profiles = useSelector(state => (managersOnly ? state.dashboard.teams : state.dashboard.profiles));

    const allRoles = useSelector(state => state.dashboard.roles);
    const roles = allRoles.filter(x => user.administrator || profiles.some(p => (p.role_Ids || []).some(r => r === x.id)));

    const teams = useSelector(state => state.dashboard.teams);

    const allDepartments = useSelector(state => state.dashboard.departments);
    const departments = allDepartments.filter(
        x => user.administrator || profiles.some(p => (p.department_Ids || []).some(d => d === x.id))
    );

    const allLocations = useSelector(state => state.dashboard.locations);
    const locations = allLocations.filter(
        x => user.administrator || profiles.some(p => (p.location_Ids || []).some(l => l === x.id))
    );

    const allBusinessUnits = useSelector(state => state.dashboard.businessUnits);
    const units = allBusinessUnits.filter(
        x => user.administrator || profiles.some(p => (p.business_Unit_Ids || []).some(b => b === x.id))
    );

    const allowed = x =>
        (!limitTypes || !limitTypes.some(t => t === x)) && (!allowMultiple || types.some(t => t.id === x && !t.singleOnly));

    const changeType = t => {
        if (!allowMultiple) {
            setProfileIds([]);
            setRoleIds([]);
            setTeamIds([]);
            setReportIds([]);
            setDepartmentIds([]);
            setLocationIds([]);
            setUnitIds([]);
        }
        setType(t);
    };

    React.useEffect(() => {
        if (value && !init) {
            setType(value.type);
            setProfileIds(value.profileIds || []);
            setRoleIds(value.roleIds || []);
            setTeamIds(value.teamIds || []);
            setReportIds(value.reportIds || []);
            setDepartmentIds(value.departmentIds || []);
            setLocationIds(value.locationIds || []);
            setUnitIds(value.unitIds || []);
        }
        setInit(true);
    }, [value]);

    React.useEffect(() => {
        init &&
            onChange &&
            onChange({
                type,
                profileIds: profileIds || [],
                roleIds: roleIds || [],
                teamIds: teamIds || [],
                reportIds: reportIds || [],
                departmentIds: departmentIds || [],
                locationIds: locationIds || [],
                unitIds: unitIds || [],
            });
    }, [type, profileIds, roleIds, teamIds, reportIds, departmentIds, locationIds, unitIds]);

    const pickButton = (x, i) => {
        const typeMatch = types.find(t => t.id === x.id);
        const count = getCount(x.id);
        return (
            allowed(x.id) &&
            getCollectionCount(x.id) > 0 && (
                <button
                    key={`pick-${x.id}`}
                    className={`btn btn-sm ${type === x.id ? 'btn-primary' : ''}`}
                    onClick={() => changeType(x.id)}
                    data-testid="button-group-picker-pick"
                    title={typeMatch.name}
                >
                    <i className={`mr5 fa fa-${typeMatch.icon}`} />
                    {typeMatch.abbr || typeMatch.name}
                    {allowMultiple && count > 0 && (
                        <span className="ml5">
                            <label className="label label-warning">{count}</label>
                        </span>
                    )}
                </button>
            )
        );
    };

    const pickButtonDropdown = (x, i) =>
        allowed(x.id) &&
        getCollectionCount(x.id) > 0 && (
            <li
                key={`pick-${i}`}
                onClick={() => {
                    //setMetricId(null);
                    setType(x.id);
                }}
                data-testid="button-group-picker-pick-dropdown"
                className="flex flex-center"
            >
                <div style={{ width: '30px' }} className="text-center">
                    <i className={`fa fa-${x.icon} mr10`} />
                </div>
                {x.name}
            </li>
        );

    const addUserOption = (data, type, value) => {
        if (!showUserOption) return data;
        const userSelected = value.some(x => x < 0);
        const name = SystemText(`General/Controls/GroupPicker/UserOption/${type}`);
        return [{ id: -1, name }].concat(
            data.map(d => {
                d.disabled = userSelected;
                return d;
            })
        );
    };

    React.useEffect(() => {
        if (profileIds.length > 1 && profileIds.some(x => x < 0)) setProfileIds([-1]);
        if (roleIds.length > 1 && roleIds.some(x => x < 0)) setRoleIds([-1]);
        if (teamIds.length > 1 && teamIds.some(x => x < 0)) setTeamIds([-1]);
        if (reportIds.length > 1 && reportIds.some(x => x < 0)) setReportIds([-1]);
        if (departmentIds.length > 1 && departmentIds.some(x => x < 0)) setDepartmentIds([-1]);
        if (locationIds.length > 1 && locationIds.some(x => x < 0)) setLocationIds([-1]);
        if (unitIds.length > 1 && unitIds.some(x => x < 0)) setUnitIds([-1]);
    }, [profileIds, roleIds, teamIds, reportIds, departmentIds, locationIds, unitIds]);

    const select = () => {
        return (
            <div>
                {type === 'P' && (
                    <Forms.Select
                        label={allowMultiple ? SystemText('General/Controls/GroupPicker/Users/Label') : null}
                        value={profileIds}
                        allowMultiple
                        onChange={setProfileIds}
                        help={showFormHelp ? SystemText('General/Controls/GroupPicker/Users/Help') : ''}
                        data={addUserOption(profiles, 'Users', profileIds)}
                        data-testid="button-group-picker-person"
                        controlId={`${controlId}-${type}`}
                    />
                )}
                {type === 'R' && (
                    <Forms.Select
                        label={allowMultiple ? SystemText('General/Controls/GroupPicker/Roles/Label') : null}
                        value={roleIds}
                        allowMultiple
                        onChange={setRoleIds}
                        help={showFormHelp ? SystemText('General/Controls/GroupPicker/Roles/Help') : ''}
                        data={addUserOption(roles, 'Roles', roleIds)}
                        data-testid="button-group-picker-role"
                        controlId={`${controlId}-${type}`}
                    />
                )}
                {type === 'T' && (
                    <Forms.Select
                        label={allowMultiple ? SystemText('General/Controls/GroupPicker/Teams/Label') : null}
                        value={teamIds}
                        allowMultiple
                        onChange={setTeamIds}
                        help={showFormHelp ? SystemText('General/Controls/GroupPicker/Teams/Help') : ''}
                        data={addUserOption(teams, 'Teams', teamIds)}
                        data-testid="button-group-picker-team"
                        controlId={`${controlId}-${type}`}
                    />
                )}
                {type === 'X' && (
                    <Forms.Select
                        label={allowMultiple ? SystemText('General/Controls/GroupPicker/Reports/Label') : null}
                        value={reportIds}
                        allowMultiple
                        onChange={setReportIds}
                        help={showFormHelp ? SystemText('General/Controls/GroupPicker/Reports/Help') : ''}
                        data={addUserOption(teams, 'Reports', reportIds)}
                        data-testid="button-group-picker-team"
                        controlId={`${controlId}-${type}`}
                    />
                )}
                {type === 'D' && (
                    <Forms.Select
                        label={allowMultiple ? SystemText('General/Controls/GroupPicker/Departments/Label') : null}
                        value={departmentIds}
                        allowMultiple
                        onChange={setDepartmentIds}
                        help={showFormHelp ? SystemText('General/Controls/GroupPicker/Departments/Help') : ''}
                        data={addUserOption(departments, 'Departments', departmentIds)}
                        data-testid="button-group-picker-department"
                        controlId={`${controlId}-${type}`}
                    />
                )}
                {type === 'L' && (
                    <Forms.Select
                        label={allowMultiple ? SystemText('General/Controls/GroupPicker/Locations/Label') : null}
                        value={locationIds}
                        allowMultiple
                        onChange={setLocationIds}
                        help={showFormHelp ? SystemText('General/Controls/GroupPicker/Locations/Help') : ''}
                        data={addUserOption(locations, 'Locations', locationIds)}
                        data-testid="button-group-picker-location"
                        controlId={`${controlId}-${type}`}
                    />
                )}
                {type === 'B' && (
                    <Forms.Select
                        label={allowMultiple ? SystemText('General/Controls/GroupPicker/BusinessUnits/Label') : null}
                        value={unitIds}
                        allowMultiple
                        onChange={setUnitIds}
                        help={showFormHelp ? SystemText('General/Controls/GroupPicker/BusinessUnits/Help') : ''}
                        data={addUserOption(units, 'BusinessUnits', unitIds)}
                        data-testid="button-group-picker-businessunit"
                        controlId={`${controlId}-${type}`}
                    />
                )}
            </div>
        );
    };

    return (
        <div data-testid="widget-group-picker">
            {allowMultiple ? (
                <Forms.Buttons
                    data-testid="button-group-picker-type"
                    label={label || defaultLabel}
                    error={error}
                    mandatory={mandatory}
                    help={!showFormHelp ? '' : help || SystemText('General/Controls/GroupPicker/Help')}
                >
                    {types.map((t, i) => pickButton(t, i))}
                </Forms.Buttons>
            ) : (
                <div className={`form-group ${error && 'has-error'}`}>
                    <label className="mb5">
                        {label || defaultLabel}
                        {mandatory && <span className="mandatory">*</span>}
                    </label>
                    {help && showFormHelp && <span className="form-control-help">{help}</span>}
                    <div className="metric-picker mbm10 flex">
                        <FloatingMenu buttonText={SystemText('General/Controls/GroupPicker/Select', typeName)}>
                            {types.map((t, i) => pickButtonDropdown(t, i))}
                        </FloatingMenu>
                        <div className="flex-grow ml10">{select()}</div>
                    </div>
                    {error && <span className="help-block mt0">{error}</span>}
                </div>
            )}
            {allowMultiple && select()}
        </div>
    );
};

export default GroupPicker;
